/**
 * THE CANONICAL PROFILE-PHOTO READ CONTRACT.
 *
 * ONE photo per PERSON, held on `app_user` and reached through this module.
 * Tasks, Scheduling, the Org Chart, Supervision and Messaging DO NOT define
 * avatar fields of their own — not an `assigneeAvatarUrl`, not a
 * `supervisorPhoto`, not a per-domain copy of the bytes, and not a second
 * cached URL. A domain that references a person carries that person's
 * `app_user.id` and nothing more; this module turns it into something to render.
 *
 * WHY THE RULE IS ABSOLUTE. A per-domain avatar field is a second copy of a
 * fact that changes, and copies of a changing fact go stale independently: the
 * roster would show the new photo and the task board the old one, and there
 * would be no single place to remove a photo from. It is also a second place
 * for a credential-free reference to become a durable URL — which is how an
 * organization-facing staff photo becomes an unrestricted global avatar.
 *
 * WHAT A SURFACE NEEDS, AND ALL IT NEEDS:
 *
 *   userId          the person's `app_user.id`. NOT a `worker.id`, NOT a
 *                   `client.id`, NOT an assignment id. Null is legitimate: a
 *                   workforce record with no login has nobody who could have
 *                   uploaded a photo, and renders initials.
 *   displayName     for the initials fallback. Always also rendered as TEXT
 *                   next to the avatar — see the accessibility rule below.
 *   photoUpdatedAt  the version, ISO, from whichever projection the surface
 *                   already reads. NULL MEANS NO PHOTO: render initials and do
 *                   not request the URL. It is never a "loading" state and
 *                   never a reason to show a placeholder face.
 *
 * See docs/PROFILE-PHOTO-CONTRACT.md for the full contract, including the
 * authorization and serving rules a new surface must not re-invent.
 */
/**
 * The URL to render, or NULL when the person has no photo and the surface must
 * fall back to initials.
 *
 * It is an authenticated, org-scoped API path, not a durable link: the session
 * cookie the app already sends is the credential, and the URL is worthless
 * without it. Do not copy it into an export, an email, a CDN, or a JSON payload
 * that leaves the session.
 *
 * CACHE BUSTING. `photoUpdatedAt` changes exactly when the photo does, so the
 * URL changes exactly when the photo does and a replaced photo is never shown
 * stale. The response also carries an ETag, so a repeated fetch of an unchanged
 * photo is a 304 with no bytes.
 *
 * A MISSING VERSION IS "NO PHOTO", NOT "MAYBE". There is deliberately no
 * version-less form: see {@link PersonPhotoSubject.photoUpdatedAt}.
 */
export function personPhotoSrc(subject) {
    const { userId, photoUpdatedAt } = subject;
    if (!userId || !photoUpdatedAt)
        return null;
    return `/users/${userId}/photo?v=${encodeURIComponent(photoUpdatedAt)}`;
}
/**
 * True when this person is known to have a photo. A surface that wants to lay
 * out differently (reserving space, choosing a tone) asks this rather than
 * inspecting the URL.
 */
export function hasPhoto(subject) {
    return Boolean(subject.userId) && Boolean(subject.photoUpdatedAt);
}
//# sourceMappingURL=photos.js.map